import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { pageSchema } from "../../schemas/pageSchema";
import { FileText, Link as LinkIcon } from "lucide-react";
import Select from "react-select";
import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useContents } from "../../hooks/useContents";

export default function PageForm({
  mode,
  defaultValues = {},
  onSubmit,
  onCancel,
  submissionLoading = false,
}) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    reset,
  } = useForm({
    resolver: yupResolver(pageSchema),
    defaultValues: {
      title: defaultValues.title || "",
      path: defaultValues.path || "",
      contentIds: defaultValues.content
        ? defaultValues.content.map((c) => c.content_id)
        : [],
      mode,
    },
  });

  useEffect(() => {
    reset({
      title: defaultValues.title || "",
      path: defaultValues.path || "",
      contentIds: defaultValues.content
        ? defaultValues.content.map((c) => c.content_id)
        : [],
      mode,
    });
  }, [defaultValues, mode, reset]);

  const [currentPage, setCurrentPage] = useState(
    defaultValues.currentPage || 1
  );

  useEffect(() => {
    setCurrentPage(defaultValues.currentPage || 1);
  }, [defaultValues.currentPage]);

  const { fetchContents } = useContents({
    pagination: { page: currentPage, limit: 10 },
    sort: { field: "created_at", direction: "desc" },
  });
  const contentsQuery = useQuery({
    queryKey: ["contents", defaultValues._id, currentPage],
    queryFn: () => fetchContents(defaultValues._id),
    keepPreviousData: true,
  });

  const contentOptions = useMemo(() => {
    const fetched =
      contentsQuery.data?.data?.contents.map((content) => ({
        label: content.title,
        value: content._id,
      })) || [];
    const preSelected =
      defaultValues.content?.map((c) => ({
        label: c.title,
        value: c.content_id,
      })) || [];
    const map = new Map();
    [...preSelected, ...fetched].forEach((opt) => {
      map.set(opt.value, opt);
    });
    return Array.from(map.values());
  }, [contentsQuery.data, defaultValues.content]);

  const transformContentIdsForSubmit = (contentIds) =>
    contentIds.map((id) => ({ content_id: id, is_active: true }));

  const onSubmitHandler = (data) => {
    onSubmit({
      ...data,
      content: transformContentIdsForSubmit(data.contentIds || []),
    });
  };

  return (
    <form onSubmit={handleSubmit(onSubmitHandler)} className="space-y-6">
      <div>
        <label className="block text-sm font-medium mb-1">Page Title</label>
        <div className="relative">
          <FileText className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 mr-5" />
          <input
            {...register("title")}
            readOnly={mode === "view"}
            placeholder="Enter page title"
            className="pl-10 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.title && <p className="text-red-500 text-xs mt-1">{errors.title.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Page Path</label>
        <div className="relative">
          <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            prefix="/"
            {...register("path")}
            readOnly={mode === "view"}
            placeholder="/your-page-path"
            className="pl-10 pr-3 py-2 border rounded w-full"
          />
        </div>
        {errors.path && <p className="text-red-500 text-xs mt-1">{errors.path.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium mb-1">Select Contents</label>
        <Controller
          name="contentIds"
          control={control}
          render={({ field: { value, onChange, onBlur, ref } }) => (
            <Select
              inputRef={ref}
              isMulti
              options={contentOptions}
              value={contentOptions.filter((opt) => (value || []).includes(opt.value))}
              onChange={(selected) => onChange(selected ? selected.map((s) => s.value) : [])}
              onBlur={onBlur}
              isDisabled={mode === "view"}
              placeholder="Choose contents..."
              classNamePrefix="react-select"
              isLoading={contentsQuery.isLoading}
            />
          )}
        />
        {errors.contentIds && <p className="text-red-500 text-xs mt-1">{errors.contentIds.message}</p>}
      </div>

      <div className="flex justify-end space-x-3 pt-4 border-t">
        <button type="button" onClick={onCancel} className="px-4 py-2 border rounded text-gray-700">
          {mode === "view" ? "Close" : "Cancel"}
        </button>
        {mode !== "view" && (
          <button type="submit" disabled={submissionLoading} className="px-4 py-2 bg-primary text-white rounded flex items-center justify-center gap-2">
            {submissionLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 100 16v-4l-3 3 3 3v-4a8 8 0 01-8-8z" />
                </svg>
                <span>Loading...</span>
              </>
            ) : (
              <>{mode === "edit" ? "Save Changes" : "Create Page"}</>
            )}
          </button>
        )}
      </div>
    </form>
  );
}
